import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  getValhallaSeasonTiers,
  setValhallaClearedTier,
  setValhallaTierWave,
  type ValhallaSeasonTiers,
} from '@/client/admin.client';

interface Props {
  account: string;
  state: Record<string, unknown> | null;
  /** 적용 성공 후 부모가 getUserDetail 을 재조회하도록 트리거. */
  onApplied: () => void;
}

/** 숫자 필드 읽기. 미설정(발할라 미플레이 계정)은 null — 0 과 구분해야 시즌 경고 오탐을 막는다. */
function readOptionalNumber(state: Record<string, unknown> | null, key: string): number | null {
  const v = state?.[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function readWaveMap(state: Record<string, unknown> | null): Record<number, number> {
  const v = state?.valhallaHighestWave;
  if (!v || typeof v !== 'object') return {};
  const out: Record<number, number> = {};
  for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
    const id = Number(k);
    if (Number.isInteger(id) && typeof val === 'number') out[id] = val;
  }
  return out;
}

/**
 * 발할라 진행도 오버라이드 — 활성 시즌 기준. `UserProgressDevPanel` 안에서만 쓰이며,
 * 부모가 prod verse 에서 렌더하지 않는다(서버도 쓰기 2종을 최종 차단).
 *
 * 두 컨트롤로 나뉜다.
 * - 클리어 티어: `lastClearedValhallaTierId` 를 지정하고 이하 티어의 최고 웨이브를
 *   풀웨이브로 채운다(해금 + 소탕 자격 + VALHALLA_WAVE_REACH·VALHALLA_ENTER 퀘스트 조건이
 *   함께 열림). 0 = 리셋. 웨이브 맵을 통째로 재작성하므로 confirm 게이트를 둔다.
 * - 최고 웨이브: 티어 하나의 도달 웨이브를 조정(부분 도달 재현). 총 웨이브 수까지 채우면
 *   해금도 함께 전진한다 — 클라 배지("클리어")와 잠금 판정이 서로 다른 필드를 보기 때문.
 */
export function ValhallaProgressSection({ account, state, onApplied }: Props) {
  const [seasonData, setSeasonData] = useState<ValhallaSeasonTiers | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const userSeasonId = readOptionalNumber(state, 'valhallaSeasonId');
  const clearedTierId = readOptionalNumber(state, 'lastClearedValhallaTierId') ?? 0;
  const waveMap = readWaveMap(state);

  // 클리어 티어 드롭다운은 유저 현재값에서 출발한다 — 기본값을 '0 (리셋)' 으로 두면
  // 버튼 한 번에 진행도를 통째로 날리는 파괴적 기본 선택이 된다. 형제 패널
  // (UserProgressDevPanel) 과 같이 state prop 갱신 시 서버 최신값으로 재동기화한다.
  const [clearTierId, setClearTierId] = useState<string>(String(clearedTierId));
  const [waveTierId, setWaveTierId] = useState<string>('');
  const [waveInput, setWaveInput] = useState<string>('1');
  const [busy, setBusy] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);

  useEffect(() => {
    setClearTierId(String(readOptionalNumber(state, 'lastClearedValhallaTierId') ?? 0));
  }, [state]);

  useEffect(() => {
    let cancelled = false;
    getValhallaSeasonTiers()
      .then((data) => {
        if (cancelled) return;
        setSeasonData(data);
        // 웨이브 컨트롤의 기본 선택 — 세트 첫 티어.
        if (data.tiers.length > 0) setWaveTierId(String(data.tiers[0].id));
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const season = seasonData?.season ?? null;
  const tiers = seasonData?.tiers ?? [];
  // 유저 스탬프가 활성 시즌과 다르면 현재 진행도는 이전 시즌 것 — 게임의 syncValhallaSeason
  // 과 마찬가지로 설정 시 리셋된 상태 위에 적용된다. 스탬프 자체가 없는(발할라 미플레이)
  // 계정은 날릴 진행도가 없으므로 경고 대상이 아니다.
  const staleSeason = season != null && userSeasonId != null && userSeasonId !== season.id;
  const selectedWaveTier = tiers.find((t) => String(t.id) === waveTierId);
  const disabled = busy || season == null;

  const run = async (fn: () => Promise<unknown>) => {
    setApplyError(null);
    setBusy(true);
    try {
      await fn();
      onApplied();
    } catch (err) {
      setApplyError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleSetClearedTier = () => {
    const target = Number(clearTierId);
    // 웨이브 맵을 통째로 재작성하는 파괴적 조작 — 형제 패널(UserRecordsPanel·
    // UserSpecTransferPanel)과 같이 confirm 게이트를 둔다.
    const detail =
      target === 0
        ? '발할라 진행도를 전부 리셋합니다(클리어 티어 0 + 최고웨이브 기록 삭제).'
        : `클리어 티어를 #${target} 로 설정하고, 이하 티어의 최고웨이브를 풀웨이브로 덮어씁니다(그 위 티어 기록은 삭제).`;
    if (!window.confirm(`${detail}\n\n대상: ${account}\n계속할까요?`)) return;
    return run(() => setValhallaClearedTier(account, target));
  };

  // 티어를 바꾸면 남아있던 입력값이 새 티어에선 풀웨이브가 될 수 있다(예: #8 의 4 →
  // #6 은 2웨이브). 풀웨이브는 해금까지 전진시키므로 조용히 넘어가면 사고가 된다 — 새 티어
  // 상한으로 즉시 클램프한다.
  const handleWaveTierChange = (nextTierId: string) => {
    setWaveTierId(nextTierId);
    const max = tiers.find((t) => String(t.id) === nextTierId)?.waveCount;
    const n = Number(waveInput);
    if (max != null && Number.isInteger(n) && n > max) setWaveInput(String(max));
  };

  const handleSetWave = () => {
    const n = Number(waveInput);
    if (!Number.isInteger(n) || n < 0) {
      setApplyError('웨이브는 0 이상의 정수여야 합니다');
      return;
    }
    // 서버도 클램프하지만, 클램프 결과가 풀웨이브면 해금까지 전진하므로 오타 하나가
    // 의도치 않은 해금이 된다(0 으로 되돌려도 해금은 복구되지 않음). 여기서 먼저 막는다.
    if (selectedWaveTier && n > selectedWaveTier.waveCount) {
      setApplyError(
        `티어 #${selectedWaveTier.id} 의 최대 웨이브는 ${selectedWaveTier.waveCount} 입니다`
      );
      return;
    }
    return run(() => setValhallaTierWave(account, Number(waveTierId), n));
  };

  return (
    <div className="flex flex-col gap-3">
      <Label className="text-xs text-muted-foreground">발할라 (Valhalla)</Label>

      {loadError && <p className="text-xs text-destructive">시즌 조회 실패: {loadError}</p>}
      {!loadError && seasonData == null && (
        <p className="text-xs text-muted-foreground">시즌 정보 로딩 중…</p>
      )}
      {seasonData != null && season == null && (
        <p className="text-xs text-muted-foreground">
          활성 발할라 시즌 없음 — 진행도를 설정할 기준 시즌이 없습니다
        </p>
      )}

      {season != null && (
        <>
          <div className="flex flex-col gap-0.5">
            <span className="text-xs font-mono text-muted-foreground">
              활성 시즌 · #{season.id} · ~{new Date(season.endTime).toLocaleString()} 종료 · 티어{' '}
              {tiers.length}개
            </span>
            <span className="text-xs font-mono text-muted-foreground">
              현재 클리어 티어 {clearedTierId} · 최고웨이브{' '}
              {Object.keys(waveMap).length === 0
                ? '없음'
                : Object.entries(waveMap)
                    .map(([id, w]) => `#${id}:${w}`)
                    .join(' ')}
            </span>
            {staleSeason && (
              <span className="text-xs text-amber-600">
                유저 진행도가 다른 시즌(#{userSeasonId}) 기준 — 설정 시 리셋됩니다
              </span>
            )}
          </div>

          {/* 클리어 티어 */}
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex flex-col gap-1">
              <Label htmlFor="valhalla-clear-tier" className="text-xs text-muted-foreground">
                클리어 티어 (lastClearedValhallaTierId)
              </Label>
              <select
                id="valhalla-clear-tier"
                value={clearTierId}
                onChange={(e) => setClearTierId(e.target.value)}
                disabled={disabled}
                className="h-9 w-56 rounded-md border border-input bg-background px-2 text-sm"
              >
                <option value="0">0 (리셋)</option>
                {tiers.map((t, idx) => (
                  <option key={t.id} value={String(t.id)}>
                    {idx + 1}번째 티어 #{t.id} (wave {t.waveCount})
                  </option>
                ))}
              </select>
            </div>
            <Button type="button" size="sm" onClick={handleSetClearedTier} disabled={disabled}>
              {busy ? '…' : '티어 설정'}
            </Button>
            <span className="text-xs text-muted-foreground">
              이하 티어 최고웨이브를 풀웨이브로 덮어쓰고 그 위 기록은 삭제
            </span>
          </div>

          {/* 티어별 최고 웨이브 */}
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex flex-col gap-1">
              <Label htmlFor="valhalla-wave-tier" className="text-xs text-muted-foreground">
                티어
              </Label>
              <select
                id="valhalla-wave-tier"
                value={waveTierId}
                onChange={(e) => handleWaveTierChange(e.target.value)}
                disabled={disabled}
                className="h-9 w-56 rounded-md border border-input bg-background px-2 text-sm"
              >
                {tiers.map((t, idx) => (
                  <option key={t.id} value={String(t.id)}>
                    {idx + 1}번째 티어 #{t.id}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="valhalla-wave" className="text-xs text-muted-foreground">
                최고 웨이브
              </Label>
              <Input
                id="valhalla-wave"
                type="number"
                min={0}
                max={selectedWaveTier?.waveCount}
                value={waveInput}
                onChange={(e) => setWaveInput(e.target.value)}
                disabled={disabled}
                className="w-24"
              />
            </div>
            <Button type="button" size="sm" onClick={handleSetWave} disabled={disabled}>
              {busy ? '…' : '최고웨이브 설정'}
            </Button>
            <span className="text-xs text-muted-foreground">
              {selectedWaveTier
                ? `현재 ${waveMap[selectedWaveTier.id] ?? 0} / 최대 ${selectedWaveTier.waveCount} · 최대치면 해금도 함께 전진 · 0 = 기록 삭제`
                : '최대치면 해금도 함께 전진 · 0 = 기록 삭제'}
            </span>
          </div>
        </>
      )}

      {applyError && <p className="text-xs text-destructive">{applyError}</p>}
    </div>
  );
}

export default ValhallaProgressSection;
